import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";

import PatternGrid from "../components/PatternGrid";
import PatternOffset from "../components/PatternOffset";
import BeadString from "../components/BeadString";
import BeadOrder from "../components/BeadOrder";
import PatternGenerator from "../components/PatternGenerator";
import DissolvePanel from "../components/DissolvePanel";
import PalettesPanel from "../components/PalettesPanel";
import * as api from "../lib/api";
import {
  normalizeHex,
  beadColorKey,
  flipPatternColorRoles,
  canFlipPatternColorRoles,
  replacePatternColor,
  recolorPatternWithPaletteColors,
  paletteColorsForRecolor,
  orderedBeadColorKeys,
  previewRoleColors,
} from "../lib/harmonicPalette";
import {
  groupConsecutiveColors,
  coloredBeadsBeforeRow,
  coloredBeadsFromStringingStart,
  detectStringingSequence,
  fillFromStringingStart,
  splitRunsAtBead,
} from "../lib/stringingOrder";
import {
  resizePatternToInches,
  rowsForLengthInches,
  rowsPreservingOffsetPhase,
} from "../lib/patternLength";
import { captureBeadOrderByPage } from "../lib/stringingOrderPdf";
import { buildStringingCrossHighlight } from "../lib/stringingCrossHighlight";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

function chartSnapshot(pattern, cols, rows, keptRows) {
  return {
    pattern: Array.isArray(pattern) ? pattern.slice() : [],
    cols,
    rows,
    keptRows: keptRows ?? null,
  };
}

function sameChart(chart, origin) {
  if (!origin) return false;
  if (chart.cols !== origin.cols || chart.rows !== origin.rows) return false;
  if ((chart.keptRows ?? null) !== (origin.keptRows ?? null)) return false;
  const left = chart.pattern || [];
  const right = origin.pattern || [];
  if (left.length !== right.length) return false;
  for (let i = 0; i < left.length; i += 1) {
    if (left[i] !== right[i]) return false;
  }
  return true;
}

function countDistinctBeadColors(pattern, defaultColor) {
  if (!Array.isArray(pattern)) return 0;
  const defCanon =
    normalizeHex(defaultColor) ||
    String(defaultColor || "")
      .trim()
      .toLowerCase();
  const seen = new Set();
  for (const raw of pattern) {
    if (raw == null || raw === "") continue;
    const canon = normalizeHex(raw);
    const c = canon || String(raw).trim().toLowerCase();
    if (!c || c === defCanon) continue;
    seen.add(c);
  }
  return seen.size;
}

function patternHasColoredBeads(pattern, defaultColor) {
  return countDistinctBeadColors(pattern, defaultColor) > 0;
}

/** "6 - Starbursts (4)" → "Starbursts - 4 colors" for PDF headers. */
function formatPdfPatternTitle(rawName, colorCount) {
  let name = String(rawName || "Pattern").trim();
  name = name.replace(/^\d+\s*[-–—]\s*/, "");
  name = name.replace(/\s*\(\d+\)\s*$/, "").trim();
  if (!name) name = "Pattern";
  const n = Math.max(0, Number(colorCount) || 0);
  const colorLabel = n === 1 ? "1 color" : `${n} colors`;
  return `${name} - ${colorLabel}`;
}

function PatternThumb({ pattern, cols, rows, defaultColor }) {
  const width = Math.max(1, Math.floor(Number(cols)) || 1);
  const list = Array.isArray(pattern) ? pattern : [];
  const height = Math.max(
    1,
    Math.floor(Number(rows)) || Math.ceil(list.length / width) || 1
  );
  const shown = Math.min(height, 12);
  const startRow = height - shown;
  const cells = [];
  for (let row = height - 1; row >= startRow; row -= 1) {
    for (let col = 0; col < width; col += 1) {
      cells.push(list[row * width + col] || defaultColor);
    }
  }

  return (
    <div
      className="pattern-thumb"
      style={{ gridTemplateColumns: `repeat(${width}, 7px)` }}
      aria-hidden="true"
    >
      {cells.map((color, index) => (
        <span
          key={index}
          className="pattern-thumb__cell"
          style={{ backgroundColor: color }}
        />
      ))}
    </div>
  );
}

const Home = () => {
  const defaultColor = "#e6e6e6";

  // Width, corresponds with "beads around"
  const [cols, setCols] = useState(6);

  // Pattern height. The rows field keeps a draft so a second digit is not
  // applied, and the chart does not jump, until the edit is finished.
  const [rows, setRows] = useState(30);
  const [rowsText, setRowsText] = useState("30");
  const rowsFieldFocused = useRef(false);
  const [lengthText, setLengthText] = useState("12");
  const lengthFallbackRef = useRef("12");

  // Array of hex values - used to display grids
  //  const [pattern, setPattern] = useState(Array(cols * rows).fill(null));
  const [pattern, setPattern] = useState([]);
  // Chart before Tweak, Distort, or Dissolve. Later edits of that chart
  // become the new original; those three tools do not.
  const [patternOrigin, setPatternOrigin] = useState(() =>
    chartSnapshot([], 6, 30, null)
  );

  // Collapsed array of hex, number arrays - used to display string order
  const [stringOrder, setStringOrder] = useState([]);

  // Repeatable segment
  const [sequence, setSequence] = useState(null);

  // Which control tab is showing: "create" | "palettes"
  const [activeControlTab, setActiveControlTab] = useState("create");
  const [isDissolveModalOpen, setIsDissolveModalOpen] = useState(false);
  const [isLibraryOpen, setIsLibraryOpen] = useState(false);
  const [libraryQuery, setLibraryQuery] = useState("");

  // Color currently used for painting
  const [activeColor, setActiveColor] = useState("#e6e6e6");

  const [letterMap, setLetterMap] = useState(null);
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [saveError, setSaveError] = useState("");
  const [isSaveAsNew, setIsSaveAsNew] = useState(false);
  const [savedPalettes, setSavedPalettes] = useState([]);
  const [selectedPaletteId, setSelectedPaletteId] = useState("");
  const [activePaletteColors, setActivePaletteColors] = useState(null);
  // Chart rows that belong to the pattern before the latest dissolve.
  const [dissolveKeptRows, setDissolveKeptRows] = useState(null);
  // Linked highlight between the bead chart and the stringing list.
  // When set, grid focus highlights only that bead; list still uses linkedRunKey.
  const [linkedRunKey, setLinkedRunKey] = useState(null);
  const [focusedBeadIndex, setFocusedBeadIndex] = useState(null);
  const [hiddenStringingGroups, setHiddenStringingGroups] = useState(
    () => new Set()
  );
  const [savedPatterns, setSavedPatterns] = useState([]);
  const [currentPatternId, setCurrentPatternId] = useState(null);
  const [currentPatternName, setCurrentPatternName] = useState(null);
  const [selectedPatternId, setSelectedPatternId] = useState("");
  const [savedFilterCols, setSavedFilterCols] = useState("");
  const [savedFilterColorCount, setSavedFilterColorCount] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const atPatternOrigin = sameChart(
    { pattern, cols, rows, keptRows: dissolveKeptRows },
    patternOrigin
  );

  const adoptPatternOrigin = (nextPattern, nextCols, nextRows, nextKeptRows) => {
    setPatternOrigin(
      chartSnapshot(nextPattern, nextCols, nextRows, nextKeptRows)
    );
  };

  const adoptPatternOriginIfCurrent = (
    nextPattern,
    nextCols,
    nextRows,
    nextKeptRows
  ) => {
    if (!atPatternOrigin) return;
    adoptPatternOrigin(nextPattern, nextCols, nextRows, nextKeptRows);
  };

  // Rotate pattern displays (visual only, in degrees: 0, 90, 180, 270)
  // Changing a block in a grid updates the pattern globally
  const updatePattern = ({ bead, color }) => {
    const newPattern = [...pattern];
    newPattern[bead - 1] = color;
    adoptPatternOriginIfCurrent(newPattern, cols, rows, dissolveKeptRows);
    setPattern(newPattern);
  };

  const handleFlipColorRoles = () => {
    if (!canFlipPatternColorRoles(pattern, defaultColor, activePaletteColors)) {
      return;
    }
    const next = flipPatternColorRoles(
      pattern,
      defaultColor,
      activePaletteColors
    );
    adoptPatternOriginIfCurrent(next, cols, rows, dissolveKeptRows);
    setPattern(next);
  };

  const handleReplacePatternColor = (fromColor, toColor) => {
    const nextHex = normalizeHex(toColor);
    const fromKey = beadColorKey(fromColor);
    const defaultKey = beadColorKey(defaultColor);
    if (!nextHex || !fromKey || fromKey === nextHex) return;
    if (defaultKey != null && fromKey === defaultKey) return;

    const next = replacePatternColor(pattern, defaultColor, fromColor, nextHex);
    adoptPatternOriginIfCurrent(next, cols, rows, dissolveKeptRows);
    setPattern(next);
    setActiveColor((prev) =>
      beadColorKey(prev) === fromKey ? nextHex : prev
    );
    setActivePaletteColors((prev) => {
      if (!Array.isArray(prev) || prev.length === 0) return prev;
      let changed = false;
      const next = prev.map((color) => {
        if (beadColorKey(color) !== fromKey) return color;
        changed = true;
        return nextHex;
      });
      return changed ? next : prev;
    });
  };

  useEffect(() => {
    // Always build the guide from the full chart. `sequence` is only the
    // detected repeat unit for "Copy Repeat" and must not truncate the guide.
    const colors = coloredBeadsFromStringingStart(
      pattern,
      cols,
      rows,
      defaultColor
    );
    setStringOrder(colors.length > 0 ? groupConsecutiveColors(colors) : []);

    // Letter map for stringing: active palette first, then extras on the grid.
    const paletteHexes = previewRoleColors(
      pattern,
      defaultColor,
      activePaletteColors
    );
    const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const hexToLetterMap = paletteHexes.reduce((map, hex, index) => {
      map[hex] = alphabet[index];
      return map;
    }, {});

    setLetterMap(hexToLetterMap);

    const defaultCanon =
      normalizeHex(defaultColor) ||
      String(defaultColor || "")
        .trim()
        .toLowerCase();

    setActiveColor((prev) => {
      if (paletteHexes.length === 0) {
        return defaultColor;
      }
      const prevCanon =
        normalizeHex(prev) ||
        String(prev || "")
          .trim()
          .toLowerCase();
      if (!prevCanon || prevCanon === defaultCanon) {
        return defaultColor;
      }
      const stillThere = paletteHexes.some(
        (h) => (normalizeHex(h) || h) === prevCanon
      );
      return stillThere ? prev : paletteHexes[0];
    });
  }, [pattern, cols, rows, defaultColor, activePaletteColors]);

  const handlePatternConvert = (patternData) => {
    // Apply the pattern
    const totalBeads = cols * rows;
    const newPattern = [];

    // Fill the pattern
    for (let i = 0; i < totalBeads; i++) {
      if (i < patternData.length) {
        newPattern.push(patternData[i]);
      } else {
        newPattern.push(defaultColor);
      }
    }

    setPattern(newPattern);
    setDissolveKeptRows(null);
    adoptPatternOrigin(newPattern, cols, rows, null);
  };

  // Tweak and Distort change the chart without replacing the original.
  const handleAdjustedPattern = (patternData) => {
    const totalBeads = cols * rows;
    const newPattern = [];
    for (let i = 0; i < totalBeads; i++) {
      if (i < patternData.length) {
        newPattern.push(patternData[i]);
      } else {
        newPattern.push(defaultColor);
      }
    }
    setPattern(newPattern);
    setDissolveKeptRows(null);
  };

  const handleResetPattern = () => {
    setPattern(patternOrigin.pattern.slice());
    setCols(patternOrigin.cols);
    setRows(patternOrigin.rows);
    setDissolveKeptRows(patternOrigin.keptRows);
    setHiddenStringingGroups(new Set());
  };

  const handleDissolve = (next) => {
    setPattern(next.pattern);
    setRows(next.rows);
    setCols(next.cols);
    setSequence(null);
    setHiddenStringingGroups(new Set());
    setDissolveKeptRows(Number.isInteger(next.keptRows) ? next.keptRows : null);
  };

  const lengthInches = parseFloat(lengthText);
  const targetLengthRows = rowsPreservingOffsetPhase(
    rowsForLengthInches(lengthInches),
    rows,
    cols
  );

  useEffect(() => {
    if (rowsFieldFocused.current) return;
    setRowsText(String(rows));
  }, [rows]);

  const commitRowsText = () => {
    const parsed = parseInt(rowsText.trim(), 10);
    if (!Number.isFinite(parsed) || parsed < 1) {
      setRowsText(String(rows));
      return;
    }
    if (parsed !== rows) {
      adoptPatternOriginIfCurrent(pattern, cols, parsed, null);
      setRows(parsed);
      setDissolveKeptRows(null);
    }
    setRowsText(String(parsed));
  };

  const commitLengthText = () => {
    const trimmed = lengthText.trim();
    const inches = parseFloat(trimmed);
    if (inches > 0) {
      lengthFallbackRef.current = trimmed;
      if (trimmed !== lengthText) setLengthText(trimmed);
      return;
    }
    setLengthText(lengthFallbackRef.current);
  };

  const handleSetLength = () => {
    if (targetLengthRows < 1 || targetLengthRows === rows) return;
    const next = resizePatternToInches({
      pattern,
      cols,
      rows,
      inches: lengthInches,
      defaultColor,
    });
    if (next.addedRows < 1 && next.removedRows < 1) return;
    adoptPatternOriginIfCurrent(next.pattern, cols, next.rows, null);
    setPattern(next.pattern);
    setRows(next.rows);
    setSequence(null);
    setHiddenStringingGroups(new Set());
    setDissolveKeptRows(null);
  };

  const handleSaveToFile = async (name) => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setSaveError("Please enter a pattern name.");
      return;
    }

    try {
      setIsLoading(true);
      // If saving as new, or if no currentPatternId, create new pattern
      if (isSaveAsNew || !currentPatternId) {
        // Create new pattern
        const newPattern = await api.createPattern({
          name: trimmedName,
          cols,
          rows,
          pattern,
          sequence: sequence || null,
          string_order: stringOrder.length > 0 ? stringOrder : null,
        });
        setCurrentPatternId(newPattern.id);
        setCurrentPatternName(trimmedName);
        setSelectedPatternId(newPattern.id.toString());
      } else {
        // Update existing pattern
        await api.updatePattern(currentPatternId, {
          name: trimmedName,
          cols,
          rows,
          pattern,
          sequence,
          string_order: stringOrder,
        });
        setCurrentPatternName(trimmedName);
      }
      await loadSavedPatterns();
      setSaveError("");
      setSaveName("");
      setIsSaveModalOpen(false);
      setIsSaveAsNew(false);
    } catch (error) {
      console.error("Failed to save pattern:", error);
      setSaveError("Failed to save pattern. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const loadSavedPatterns = async () => {
    try {
      const patterns = await api.getPatterns();
      setSavedPatterns(patterns || []);
    } catch (error) {
      console.error("Failed to load patterns:", error);
      // Silently fail - backend might not be running
      setSavedPatterns([]);
    }
  };

  const loadSavedPalettes = async () => {
    try {
      const list = await api.getColorPalettes();
      setSavedPalettes(Array.isArray(list) ? list : []);
      setSelectedPaletteId((prev) =>
        prev && (list || []).some((palette) => String(palette.id) === String(prev))
          ? prev
          : ""
      );
    } catch (error) {
      console.error("Failed to load color palettes:", error);
      setSavedPalettes([]);
    }
  };

  const handleCreatePalette = async (name, colors) => {
    try {
      setIsLoading(true);
      const created = await api.createColorPalette({ name, colors });
      await loadSavedPalettes();
      if (created?.id != null) {
        setSelectedPaletteId(String(created.id));
      }
    } catch (error) {
      console.error("Failed to create palette:", error);
      setSaveError("Failed to create palette. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRenamePalette = async (id, name) => {
    try {
      setIsLoading(true);
      await api.updateColorPalette(id, { name });
      await loadSavedPalettes();
    } catch (error) {
      console.error("Failed to rename palette:", error);
      setSaveError("Failed to rename palette. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSavePaletteColors = async (id, colors) => {
    try {
      await api.updateColorPalette(id, { colors });
      await loadSavedPalettes();
    } catch (error) {
      console.error("Failed to save palette colors:", error);
      setSaveError("Failed to save palette colors. Please try again.");
    }
  };

  const handleDeletePalette = async (id, name) => {
    if (
      !window.confirm(`Delete palette "${name}"? This cannot be undone.`)
    ) {
      return;
    }
    try {
      setIsLoading(true);
      await api.deleteColorPalette(id);
      await loadSavedPalettes();
      if (String(selectedPaletteId) === String(id)) {
        setSelectedPaletteId("");
      }
    } catch (error) {
      console.error("Failed to delete palette:", error);
      setSaveError("Failed to delete palette. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleApplyPaletteToPattern = (colors) => {
    const norm = paletteColorsForRecolor(colors, defaultColor);
    if (norm.length === 0) return;
    setActivePaletteColors(norm);
    if (norm[0]) setActiveColor(norm[0]);
    if (patternHasColoredBeads(pattern, defaultColor)) {
      const next = recolorPatternWithPaletteColors(pattern, defaultColor, norm);
      adoptPatternOriginIfCurrent(next, cols, rows, dissolveKeptRows);
      setPattern(next);
    }
  };

  const beginBlankTemplate = (around, { resetRows = false } = {}) => {
    const nextCols = Math.max(1, Math.floor(Number(around)) || 1);
    const nextRows = resetRows ? 30 : rows;
    setCols(nextCols);
    if (resetRows) setRows(30);
    setPattern([]);
    adoptPatternOrigin([], nextCols, nextRows, null);
    setSequence(null);
    setStringOrder([]);
    setHiddenStringingGroups(new Set());
    setDissolveKeptRows(null);
    setCurrentPatternId(null);
    setCurrentPatternName(null);
    setSelectedPatternId("");
  };

  const handleBeadsAroundFilter = (value) => {
    setSavedFilterCols(value);
    const around = Number(value);
    if (!Number.isFinite(around) || around <= 0) return;

    const loaded = currentPatternId
      ? savedPatterns.find((item) => String(item.id) === String(currentPatternId))
      : null;
    if (loaded && Number(loaded.cols) === around) return;

    const blank = !patternHasColoredBeads(pattern, defaultColor);
    if (!blank && !loaded) return;

    beginBlankTemplate(around, { resetRows: Boolean(loaded) });
  };

  const applyLoadedPattern = (loaded, { libraryPatternId = null } = {}) => {
    setCols(loaded.cols);
    setRows(loaded.rows);
    setPattern(loaded.pattern || []);
    adoptPatternOrigin(loaded.pattern || [], loaded.cols, loaded.rows, null);
    setSequence(loaded.sequence || null);
    setStringOrder(loaded.string_order || []);
    setDissolveKeptRows(null);
    setCurrentPatternId(libraryPatternId);
    setCurrentPatternName(loaded.name || null);
    setSelectedPatternId(libraryPatternId ? String(libraryPatternId) : "");

    const patternColors = loaded.pattern || [];
    const uniqueColors = [...new Set(patternColors)].filter(
      (color) => color && color !== defaultColor && color.trim() !== ""
    );

    if (uniqueColors.length > 0) {
      setActiveColor(uniqueColors[0]);
      setActivePaletteColors(
        paletteColorsForRecolor(uniqueColors, defaultColor)
      );
    } else {
      setActivePaletteColors(null);
    }
  };

  const handleLoadSaved = async (patternId) => {
    try {
      const loaded = await api.getPattern(patternId);
      applyLoadedPattern(loaded, { libraryPatternId: loaded.id });
    } catch (error) {
      console.error("Failed to load pattern:", error);
      setSaveError("Failed to load pattern. Please try again.");
    }
  };

  const handleDeletePattern = async (patternId, patternName) => {
    if (
      !window.confirm(
        `Are you sure you want to delete "${patternName}"? This cannot be undone.`
      )
    ) {
      return;
    }

    try {
      await api.deletePattern(patternId);
      // Reload the patterns list
      await loadSavedPatterns();
      // Clear current pattern if it was the one deleted
      if (currentPatternId === patternId) {
        setCurrentPatternId(null);
        setCurrentPatternName(null);
      }
      // Clear selected pattern in dropdown if it was deleted
      if (selectedPatternId === patternId.toString()) {
        setSelectedPatternId("");
      }
    } catch (error) {
      console.error("Failed to delete pattern:", error);
      setSaveError("Failed to delete pattern. Please try again.");
    }
  };

  const handleDeleteSelectedPattern = () => {
    if (!selectedPatternId) return;
    const pattern = savedPatterns.find(
      (p) => p.id.toString() === selectedPatternId
    );
    if (pattern) {
      handleDeletePattern(pattern.id, pattern.name);
    }
  };

  const savedColsOptions = useMemo(() => {
    const s = new Set();
    for (const p of savedPatterns) {
      const n = Number(p.cols);
      if (Number.isFinite(n) && n > 0) s.add(n);
    }
    return [...s].sort((a, b) => a - b);
  }, [savedPatterns]);

  const savedColorCountOptions = useMemo(() => {
    const s = new Set();
    for (const p of savedPatterns) {
      s.add(countDistinctBeadColors(p.pattern, defaultColor));
    }
    return [...s].sort((a, b) => a - b);
  }, [savedPatterns, defaultColor]);

  const filteredSavedPatterns = useMemo(() => {
    const colsF = savedFilterCols === "" ? null : Number(savedFilterCols);
    const ccF =
      savedFilterColorCount === "" ? null : Number(savedFilterColorCount);
    const filtered = savedPatterns.filter((p) => {
      if (colsF != null && Number(p.cols) !== colsF) return false;
      if (
        ccF != null &&
        countDistinctBeadColors(p.pattern, defaultColor) !== ccF
      )
        return false;
      return true;
    });
    return [...filtered].sort((a, b) =>
      String(a.name || "").localeCompare(String(b.name || ""), undefined, {
        sensitivity: "base",
      })
    );
  }, [savedPatterns, savedFilterCols, savedFilterColorCount, defaultColor]);

  const effectiveSelectedSavedId = useMemo(() => {
    if (!selectedPatternId) return "";
    return filteredSavedPatterns.some(
      (p) => p.id.toString() === selectedPatternId
    )
      ? selectedPatternId
      : "";
  }, [selectedPatternId, filteredSavedPatterns]);

  const selectedSavedPattern = useMemo(
    () =>
      savedPatterns.find(
        (pattern) => String(pattern.id) === String(effectiveSelectedSavedId)
      ) || null,
    [savedPatterns, effectiveSelectedSavedId]
  );

  const libraryPatterns = useMemo(() => {
    const query = libraryQuery.trim().toLowerCase();
    if (!query) return filteredSavedPatterns;
    return filteredSavedPatterns.filter((pattern) =>
      String(pattern.name || "")
        .toLowerCase()
        .includes(query)
    );
  }, [filteredSavedPatterns, libraryQuery]);

  useEffect(() => {
    if (
      selectedPatternId &&
      !filteredSavedPatterns.some(
        (p) => p.id.toString() === selectedPatternId
      )
    ) {
      setSelectedPatternId("");
    }
  }, [selectedPatternId, filteredSavedPatterns]);

  // Load saved patterns and palettes on mount
  useEffect(() => {
    loadSavedPatterns();
    loadSavedPalettes();
  }, []);

  const handleDetectRepeat = useCallback(
    (arr) => {
      const detected =
        dissolveKeptRows == null
          ? detectStringingSequence(arr, cols, rows, defaultColor)
          : coloredBeadsFromStringingStart(arr, cols, rows, defaultColor);
      const flattened = detected && detected.length > 0 ? detected : null;
      setHiddenStringingGroups(new Set());
      setSequence(flattened);

      if (!flattened) return null;
      return {
        pattern: flattened,
        startIndex: 0,
        sequenceLength: flattened.length,
      };
    },
    [defaultColor, cols, rows, dissolveKeptRows]
  );

  // Keep stringing repeat detection in sync with the current pattern.
  useEffect(() => {
    handleDetectRepeat(pattern);
  }, [pattern, handleDetectRepeat]);

  const handleCopyPattern = () => {
    if (!sequence || sequence.length === 0) {
      return;
    }

    const next = fillFromStringingStart(
      pattern,
      sequence,
      cols,
      rows,
      defaultColor
    );
    adoptPatternOriginIfCurrent(next, cols, rows, null);
    setPattern(next);
    setDissolveKeptRows(null);
  };

  const updateColor = (hex) => {
    setActiveColor(hex);
  };

  const getLetterForHex = (hex) => {
    if (!letterMap) {
      return;
    }
    const canon = normalizeHex(hex) || String(hex || "").trim().toLowerCase();
    if (canon && letterMap[canon]) return letterMap[canon];
    return letterMap[hex] || null;
  };

  const handleOpenSaveModal = () => {
    setSaveError("");
    // Pre-populate name if editing existing pattern
    if (currentPatternId) {
      const currentPattern = savedPatterns.find(
        (p) => p.id === currentPatternId
      );
      setSaveName(currentPattern ? currentPattern.name : "");
    } else {
      setSaveName("");
    }
    setIsSaveModalOpen(true);
  };

  const handleOpenSaveAsNewModal = () => {
    setSaveError("");
    setSaveName("");
    setIsSaveAsNew(true);
    setIsSaveModalOpen(true);
  };

  const handleCloseSaveModal = () => {
    setIsSaveModalOpen(false);
    setSaveName("");
    setSaveError("");
    setIsSaveAsNew(false);
  };

  const patternPreviewRef = useRef(null);
  const stringingGuideRef = useRef(null);
  const [previewAdjustEl, setPreviewAdjustEl] = useState(null);

  const handleExportPDF = async () => {
    // Open immediately from the click gesture so popup blockers allow it.
    const pdfWindow = window.open("", "_blank");

    try {
      setIsLoading(true);
      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 12;
      const contentWidth = pageWidth - 2 * margin;
      let y = margin;

      const ensureSpace = (neededMm) => {
        if (y + neededMm > pageHeight - margin) {
          pdf.addPage();
          y = margin;
        }
      };

      /** Place a canvas image; slices across pages when taller than remaining space. */
      const addCanvasImage = (canvas, destWidthMm, xMm = margin) => {
        if (!canvas || canvas.width === 0 || canvas.height === 0) return;
        const fullHeightMm = (canvas.height * destWidthMm) / canvas.width;
        const pxPerMm = canvas.height / fullHeightMm;

        let srcY = 0;
        let remainingMm = fullHeightMm;

        while (remainingMm > 0.5) {
          const spaceMm = pageHeight - margin - y;
          if (spaceMm < 8) {
            pdf.addPage();
            y = margin;
            continue;
          }

          const sliceMm = Math.min(remainingMm, spaceMm);
          const slicePx = Math.max(1, Math.round(sliceMm * pxPerMm));
          const sliceCanvas = document.createElement("canvas");
          sliceCanvas.width = canvas.width;
          sliceCanvas.height = slicePx;
          const ctx = sliceCanvas.getContext("2d");
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
          ctx.drawImage(
            canvas,
            0,
            Math.round(srcY),
            canvas.width,
            slicePx,
            0,
            0,
            canvas.width,
            slicePx
          );

          const sliceData = sliceCanvas.toDataURL("image/png");
          const drawHeightMm = (slicePx / canvas.width) * destWidthMm;
          pdf.addImage(sliceData, "PNG", xMm, y, destWidthMm, drawHeightMm);

          y += drawHeightMm;
          srcY += slicePx;
          remainingMm -= drawHeightMm;
        }
      };

      const colorCount = countDistinctBeadColors(pattern, defaultColor);
      const patternTitle = formatPdfPatternTitle(
        currentPatternName || "Pattern",
        colorCount
      );
      pdf.setFontSize(18);
      pdf.setTextColor(20);
      pdf.text(patternTitle, margin, y + 5);
      y += 10;
      pdf.setFontSize(11);
      pdf.setTextColor(80);
      pdf.text(`${cols} beads around`, margin, y);
      y += 8;

      // Previews: compact width so the stringing pattern can dominate below.
      if (patternPreviewRef.current) {
        ensureSpace(20);
        pdf.setFontSize(12);
        pdf.setTextColor(40);
        pdf.text("Pattern Preview", margin, y);
        y += 5;

        const previewCanvas = await html2canvas(patternPreviewRef.current, {
          scale: 2,
          backgroundColor: "#ffffff",
          useCORS: true,
          logging: false,
        });
        const previewWidth = contentWidth * 0.55;
        const previewX = margin + (contentWidth - previewWidth) / 2;
        addCanvasImage(previewCanvas, previewWidth, previewX);
        y += 10;
      }

      // Stringing chart, then the column list on its own page.
      if (stringingGuideRef.current) {
        const guideEl = stringingGuideRef.current;
        const actionButtons = guideEl.querySelector(".bead-order-actions");
        const stringEl = guideEl.querySelector(".bead-string-outer");
        const orderEl = guideEl.querySelector(".bead-order");
        const originalDisplay = actionButtons
          ? actionButtons.style.display
          : null;
        if (actionButtons) {
          actionButtons.style.display = "none";
        }

        const captureOptions = {
          scale: 2,
          backgroundColor: "#ffffff",
          useCORS: true,
          logging: false,
        };

        try {
          if (stringEl) {
            ensureSpace(20);
            pdf.setFontSize(12);
            pdf.setTextColor(40);
            pdf.text("Stringing Guide", margin, y);
            y += 5;

            const stringCanvas = await html2canvas(stringEl, captureOptions);
            addCanvasImage(stringCanvas, contentWidth, margin);
          }

          if (orderEl) {
            const orderWidthPx = Math.max(
              1,
              Math.round(
                orderEl.getBoundingClientRect().width || orderEl.offsetWidth || 1
              )
            );
            const titleHeightMm = 5;
            const availableMm = pageHeight - 2 * margin - titleHeightMm;
            const pageHeightPx = Math.max(
              40,
              Math.round((availableMm * orderWidthPx) / contentWidth)
            );

            const orderPages = await captureBeadOrderByPage(orderEl, {
              pageHeightPx,
              html2canvas,
              captureOptions,
            });

            for (let pageIndex = 0; pageIndex < orderPages.length; pageIndex++) {
              pdf.addPage();
              y = margin;
              pdf.setFontSize(12);
              pdf.setTextColor(40);
              pdf.text("Stringing Order", margin, y);
              y += titleHeightMm;

              const orderCanvas = orderPages[pageIndex];
              const heightMm =
                (orderCanvas.height * contentWidth) / orderCanvas.width;
              const drawHeightMm = Math.min(
                heightMm,
                pageHeight - margin - y
              );
              pdf.addImage(
                orderCanvas.toDataURL("image/png"),
                "PNG",
                margin,
                y,
                contentWidth,
                drawHeightMm
              );
              y += drawHeightMm;
            }
          }
        } finally {
          if (actionButtons) {
            actionButtons.style.display = originalDisplay || "";
          }
        }
      }

      const blob = pdf.output("blob");
      const url = URL.createObjectURL(blob);
      if (pdfWindow && !pdfWindow.closed) {
        pdfWindow.location.href = url;
      } else {
        window.open(url, "_blank");
      }
    } catch (error) {
      console.error("Failed to export PDF:", error);
      setSaveError("Failed to export PDF. Please try again.");
      if (pdfWindow && !pdfWindow.closed) {
        pdfWindow.close();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const regenerateStringOrderFromPattern = () => {
    setHiddenStringingGroups(new Set());

    if (!pattern || pattern.length === 0) {
      setStringOrder([]);
      setSequence(null);
      return;
    }

    const colors = coloredBeadsFromStringingStart(
      pattern,
      cols,
      rows,
      defaultColor
    );

    if (colors.length === 0) {
      setStringOrder([]);
      setSequence(null);
      return;
    }

    setStringOrder(groupConsecutiveColors(colors));
    // Keep the detected repeat unit for Copy; only refresh the guide runs.
    handleDetectRepeat(pattern);
  };

  const displayStringOrder = useMemo(() => {
    if (!stringOrder || stringOrder.length === 0) {
      return [];
    }

    return stringOrder.reduce((acc, group, index) => {
      const [color, count] = group;
      if (color === defaultColor) {
        return acc;
      }
      if (hiddenStringingGroups.has(index)) {
        return acc;
      }

      acc.push({
        color,
        count,
        originalIndex: index,
      });

      return acc;
    }, []);
  }, [stringOrder, defaultColor, hiddenStringingGroups]);

  const dissolveSeam = useMemo(() => {
    if (!Number.isInteger(dissolveKeptRows)) return null;
    const count = coloredBeadsBeforeRow(
      pattern,
      cols,
      rows,
      dissolveKeptRows,
      defaultColor
    );
    return count > 0 ? count : null;
  }, [pattern, cols, rows, dissolveKeptRows, defaultColor]);

  // Group repeating patterns in the display string order
  const groupedBeadOrder = useMemo(() => {
    const groupRuns = (displayStringOrder) => {
    if (!displayStringOrder || displayStringOrder.length === 0) {
      return [];
    }

    const result = [];
    let i = 0;

    while (i < displayStringOrder.length) {
      let bestPattern = null;
      let bestScore = 0;

      // Try different pattern lengths starting from position i.
      // Cap at 12 so a 2-row checkerboard merge tile can collapse to Repeat N×.
      const maxPatternLength = Math.min(
        12,
        Math.floor((displayStringOrder.length - i) / 2)
      );

      for (
        let patternLength = 1;
        patternLength <= maxPatternLength;
        patternLength++
      ) {
        // Get the pattern to check
        const pattern = displayStringOrder.slice(i, i + patternLength);
        let repeatCount = 1;

        // Check how many times this pattern repeats consecutively
        let j = i + patternLength;
        while (j + patternLength <= displayStringOrder.length) {
          const nextPattern = displayStringOrder.slice(j, j + patternLength);

          // Check if patterns match
          const matches = pattern.every(
            (item, idx) =>
              item.color === nextPattern[idx].color &&
              item.count === nextPattern[idx].count
          );

          if (matches) {
            repeatCount++;
            j += patternLength;
          } else {
            break;
          }
        }

        // Only consider patterns that repeat at least 2 times
        if (repeatCount >= 2) {
          // Score: prioritize patterns that cover more items (length * repeats)
          const score = patternLength * repeatCount;
          if (score > bestScore) {
            bestScore = score;
            bestPattern = {
              pattern: pattern,
              repeatCount: repeatCount,
              length: patternLength,
            };
          }
        }
      }

      // If we found a good repeating pattern, use it
      if (bestPattern && bestPattern.repeatCount >= 2) {
        result.push({
          type: "repeat",
          pattern: bestPattern.pattern,
          repeatCount: bestPattern.repeatCount,
          originalIndices: bestPattern.pattern.map(
            (item) => item.originalIndex
          ),
        });
        i += bestPattern.length * bestPattern.repeatCount;
      } else {
        // No repeating pattern found, add as single item
        result.push({
          type: "single",
          item: displayStringOrder[i],
        });
        i++;
      }
    }

    return result;
    };

    if (dissolveSeam == null) return groupRuns(displayStringOrder);
    const [before, after] = splitRunsAtBead(displayStringOrder, dissolveSeam);
    if (after.length === 0) return groupRuns(displayStringOrder);
    return [...groupRuns(before), { type: "divide" }, ...groupRuns(after)];
  }, [displayStringOrder, dissolveSeam]);

  const stringingLinkMap = useMemo(
    () => buildStringingCrossHighlight(groupedBeadOrder),
    [groupedBeadOrder]
  );

  const linkedBeadIndexes = useMemo(() => {
    // Grid navigation: outline only the focused bead.
    // List navigation: outline every bead in the selected run.
    if (focusedBeadIndex != null) return [focusedBeadIndex];
    return stringingLinkMap.beadIndexesForRun(linkedRunKey);
  }, [stringingLinkMap, linkedRunKey, focusedBeadIndex]);

  const tabPanelClass = (tab) =>
    `workbench__tab-panel${
      activeControlTab === tab ? "" : " workbench__tab-panel--hidden"
    }`;

  return (
    <div className="workbench">
      <header className="workbench__bar">
        <div className="workbench__identity">
          <div className="workbench__brand">
            <span className="workbench__logo" aria-hidden="true">
              <span />
              <span />
              <span />
            </span>
            <h1>Bead Pattern Designer</h1>
          </div>
          <p className="workbench__project">
            <span
              className={`workbench__status${
                currentPatternId ? " workbench__status--saved" : ""
              }`}
            >
              {currentPatternId ? "Saved pattern" : "Unsaved"}
            </span>
            <span>{currentPatternName || "Untitled"}</span>
            <span className="workbench__dims">
              {cols} around × {rows} rows
            </span>
          </p>
        </div>
        <button
          type="button"
          className="workbench__signout"
          onClick={() => {
            api.logout().finally(() => window.location.reload());
          }}
        >
          Sign out
        </button>
      </header>

      {saveError && !isSaveModalOpen && (
        <p className="modal-error workbench__error">{saveError}</p>
      )}

      <div
        className={`workbench__body${
          activeControlTab === "palettes" ? " workbench__body--solo" : ""
        }`}
      >
        <section className="panel workbench__controls">
          <div className="workbench__tabs" role="tablist">
            <button
              type="button"
              role="tab"
              id="tab-create"
              aria-selected={activeControlTab === "create"}
              aria-controls="panel-create"
              className={`workbench__tab${
                activeControlTab === "create" ? " is-active" : ""
              }`}
              onClick={() => {
                loadSavedPalettes();
                setActiveControlTab("create");
              }}
            >
              Create
            </button>
            <button
              type="button"
              role="tab"
              id="tab-palettes"
              aria-selected={activeControlTab === "palettes"}
              aria-controls="panel-palettes"
              className={`workbench__tab${
                activeControlTab === "palettes" ? " is-active" : ""
              }`}
              onClick={() => {
                loadSavedPalettes();
                setActiveControlTab("palettes");
              }}
            >
              Palettes{savedPalettes.length > 0 ? ` (${savedPalettes.length})` : ""}
            </button>
          </div>

          {/* Keep Create mounted while hidden so generate recipe settings survive tab switches. */}
          <div
            className={tabPanelClass("create")}
            id="panel-create"
            role="tabpanel"
            aria-labelledby="tab-create"
          >
            <div className="panel__content">
              <section className="pg-section">
                <h3 className="pg-section__title">
                  Library
                  {savedPatterns.length > 0 ? ` (${savedPatterns.length})` : ""}
                </h3>
                {savedPatterns.length === 0 ? (
                  <p className="collections-empty">
                    No saved patterns yet. Use Save Pattern to add one.
                  </p>
                ) : (
                  <div className="load-buttons">
                    <div className="saved-pattern-filters">
                      <div className="saved-pattern-filters__field">
                        <label htmlFor="saved-filter-cols">Beads around</label>
                        <select
                          id="saved-filter-cols"
                          className="select-input"
                          value={savedFilterCols}
                          onChange={(e) =>
                            handleBeadsAroundFilter(e.target.value)
                          }
                        >
                          <option value="">All</option>
                          {savedColsOptions.map((c) => (
                            <option key={c} value={String(c)}>
                              {c}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="saved-pattern-filters__field">
                        <label htmlFor="saved-filter-colors">Colors</label>
                        <select
                          id="saved-filter-colors"
                          className="select-input"
                          value={savedFilterColorCount}
                          onChange={(e) =>
                            setSavedFilterColorCount(e.target.value)
                          }
                        >
                          <option value="">All</option>
                          {savedColorCountOptions.map((n) => (
                            <option key={n} value={String(n)}>
                              {n}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="button library-open"
                      aria-label="Load Saved Pattern"
                      disabled={filteredSavedPatterns.length === 0}
                      onClick={() => {
                        setLibraryQuery("");
                        setIsLibraryOpen(true);
                      }}
                    >
                      <span className="library-open__name">
                        {selectedSavedPattern
                          ? selectedSavedPattern.name
                          : filteredSavedPatterns.length === 0
                            ? "No patterns match filters"
                            : "Choose a pattern"}
                      </span>
                      <span className="library-open__hint" aria-hidden="true">
                        Browse
                      </span>
                    </button>
                    {effectiveSelectedSavedId && (
                      <button
                        type="button"
                        className="button button--danger"
                        onClick={handleDeleteSelectedPattern}
                      >
                        Delete Selected Pattern
                      </button>
                    )}
                  </div>
                )}
              </section>

              <section className="pg-section">
                <h3 className="pg-section__title">Canvas</h3>
                <div className="canvas-size">
                  <div className="workbench__field">
                    <label htmlFor="cols-input">Beads Around</label>
                    <input
                      id="cols-input"
                      type="number"
                      min={1}
                      value={cols}
                      onChange={(event) => {
                        const parsedValue = parseInt(event.target.value, 10);
                        if (Number.isNaN(parsedValue)) {
                          return;
                        }
                        const nextCols = Math.max(1, parsedValue);
                        adoptPatternOriginIfCurrent(pattern, nextCols, rows, null);
                        setCols(nextCols);
                        setDissolveKeptRows(null);
                      }}
                      className="number-input number-input--compact"
                    />
                  </div>
                  <div className="workbench__field">
                    <label htmlFor="rows-input">Rows</label>
                    <input
                      id="rows-input"
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      value={rowsText}
                      onFocus={(event) => {
                        rowsFieldFocused.current = true;
                        event.currentTarget.select();
                      }}
                      onMouseUp={(event) => event.preventDefault()}
                      onChange={(event) => setRowsText(event.target.value)}
                      onBlur={() => {
                        rowsFieldFocused.current = false;
                        commitRowsText();
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          event.currentTarget.blur();
                        }
                      }}
                      className="number-input number-input--compact"
                    />
                  </div>
                </div>
                <div className="pattern-length">
                  <div className="pg-field-row pattern-length__row">
                    <div className="workbench__field pg-field--narrow">
                      <label htmlFor="total-length-inches">
                        Total length (in)
                      </label>
                      <input
                        id="total-length-inches"
                        type="text"
                        inputMode="decimal"
                        autoComplete="off"
                        value={lengthText}
                        onFocus={(event) => event.currentTarget.select()}
                        onMouseUp={(event) => event.preventDefault()}
                        onChange={(event) => setLengthText(event.target.value)}
                        onBlur={commitLengthText}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            event.preventDefault();
                            event.currentTarget.blur();
                          }
                        }}
                        className="number-input number-input--compact"
                      />
                    </div>
                    <button
                      type="button"
                      className="button button--primary"
                      onClick={handleSetLength}
                      disabled={
                        targetLengthRows < 1 || targetLengthRows === rows
                      }
                    >
                      Set length
                    </button>
                  </div>
                </div>
              </section>

              <PatternGenerator
                onConvert={handlePatternConvert}
                cols={cols}
                rows={rows}
                pattern={pattern}
                savedPalettes={savedPalettes}
                onPalettesChanged={loadSavedPalettes}
                onActivePaletteColorsChange={setActivePaletteColors}
                onApplySavedPalette={handleApplyPaletteToPattern}
                externalPaletteColors={activePaletteColors}
                activeColor={activeColor}
                defaultColor={defaultColor}
                onSelectPaintColor={updateColor}
                onCommitPaintColor={handleReplacePatternColor}
                previewAdjustEl={previewAdjustEl}
                onOpenDissolve={() => setIsDissolveModalOpen(true)}
                onAdjust={handleAdjustedPattern}
                onResetPattern={handleResetPattern}
                canReset={!atPatternOrigin}
              />
            </div>
          </div>

          <div
            className={tabPanelClass("palettes")}
            id="panel-palettes"
            role="tabpanel"
            aria-labelledby="tab-palettes"
          >
            <PalettesPanel
              palettes={savedPalettes}
              selectedPaletteId={selectedPaletteId}
              isLoading={isLoading}
              canApplyToPattern={patternHasColoredBeads(pattern, defaultColor)}
              onSelectPalette={setSelectedPaletteId}
              onCreatePalette={handleCreatePalette}
              onRenamePalette={handleRenamePalette}
              onDeletePalette={handleDeletePalette}
              onSaveColors={handleSavePaletteColors}
              onApplyToPattern={handleApplyPaletteToPattern}
            />
          </div>

        </section>

        <div
          className={`workbench__output${
            activeControlTab === "palettes" ? " workbench__output--hidden" : ""
          }`}
          hidden={activeControlTab === "palettes"}
        >
          <section className="panel panel--preview">
            <div className="panel__header">
              <h2 className="panel__title">Pattern Preview</h2>
              <div className="panel__actions">
                <button
                  type="button"
                  className="button"
                  onClick={handleFlipColorRoles}
                  disabled={
                    !canFlipPatternColorRoles(
                      pattern,
                      defaultColor,
                      activePaletteColors
                    )
                  }
                  title={
                    activePaletteColors?.length >
                    orderedBeadColorKeys(pattern, defaultColor).length
                      ? "Rotate the pattern through every color in the selected palette."
                      : orderedBeadColorKeys(pattern, defaultColor).length === 3
                        ? "Cycles through every arrangement of the three colors."
                        : "Same colors on the grid; reverses which role is which (first distinct color swaps with last)."
                  }
                >
                  Flip color roles
                </button>
                <button
                  type="button"
                  className="button button--primary"
                  onClick={handleOpenSaveModal}
                >
                  {currentPatternId ? "Update Pattern" : "Save Pattern"}
                </button>
                {currentPatternId && (
                  <button
                    type="button"
                    className="button"
                    onClick={handleOpenSaveAsNewModal}
                  >
                    Save As New
                  </button>
                )}
              </div>
            </div>

            <div className="preview-adjust" ref={setPreviewAdjustEl} />

            <div
              className="panel__content pattern-previews"
              ref={patternPreviewRef}
            >
              <PatternGrid
                defaultColor={defaultColor}
                color={activeColor}
                pattern={pattern}
                updatePattern={updatePattern}
                cols={cols}
                rows={rows}
              />

              <PatternOffset
                defaultColor={defaultColor}
                color={activeColor}
                pattern={pattern}
                updatePattern={updatePattern}
                cols={cols}
                rows={rows}
              />
            </div>
          </section>

          <section className="panel panel--stringing">
            {/* Export PDF sits outside stringingGuideRef so it is not captured. */}
            <div className="panel__header">
              <h2 className="panel__title">Stringing Guide</h2>
              <div className="panel__actions">
                <button
                  type="button"
                  className="button"
                  onClick={handleExportPDF}
                  disabled={isLoading}
                >
                  {isLoading ? "Exporting..." : "Export PDF"}
                </button>
              </div>
            </div>
            <div
              className="panel__content panel__content--stack"
              ref={stringingGuideRef}
            >
              <div
                className="stringing-columns"
                onBlur={(event) => {
                  if (!event.currentTarget.contains(event.relatedTarget)) {
                    setLinkedRunKey(null);
                    setFocusedBeadIndex(null);
                  }
                }}
              >
                <BeadString
                  runs={displayStringOrder}
                  cols={cols}
                  defaultColor={defaultColor}
                  letterMap={letterMap}
                  divideAt={dissolveSeam}
                  linkedBeadIndexes={linkedBeadIndexes}
                  onBeadActivate={(beadIndex) => {
                    setFocusedBeadIndex(beadIndex);
                    setLinkedRunKey(stringingLinkMap.runKeyForBead(beadIndex));
                  }}
                />

                <BeadOrder
                  groups={groupedBeadOrder}
                  getLetterForHex={getLetterForHex}
                  activeRunKey={linkedRunKey}
                  onRunActivate={(runKey) => {
                    setFocusedBeadIndex(null);
                    setLinkedRunKey(runKey);
                  }}
                />
              </div>
              <div className="bead-order-actions">
                {sequence && (
                  <button
                    type="button"
                    className="button"
                    onClick={handleCopyPattern}
                  >
                    Copy Repeat to Fill Pattern
                  </button>
                )}
                <button
                  type="button"
                  className="button"
                  onClick={regenerateStringOrderFromPattern}
                >
                  Regenerate From Pattern
                </button>
              </div>
            </div>
          </section>
        </div>
      </div>
      {isSaveModalOpen && (
        <div className="modal-overlay" onClick={handleCloseSaveModal}>
          <div
            className="modal"
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            <h2>
              {isSaveAsNew
                ? "Save As New Pattern"
                : currentPatternId
                ? "Update Pattern"
                : "Save Pattern"}
            </h2>
            <form
              onSubmit={(event) => {
                event.preventDefault();
                handleSaveToFile(saveName);
              }}
              className="modal-form"
            >
              <label htmlFor="pattern-name">Pattern Name</label>
              <input
                id="pattern-name"
                type="text"
                value={saveName}
                onChange={(event) => setSaveName(event.target.value)}
                placeholder="e.g. canyon-sunrise"
                autoFocus
              />
              {saveError && <p className="modal-error">{saveError}</p>}
              <div className="modal-actions">
                <button
                  type="submit"
                  className="button button--primary"
                  disabled={isLoading}
                >
                  {isLoading ? "Saving..." : "Save"}
                </button>
                <button
                  type="button"
                  className="button"
                  onClick={handleCloseSaveModal}
                  disabled={isLoading}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {isLibraryOpen && (
        <div
          className="modal-overlay"
          onClick={() => setIsLibraryOpen(false)}
        >
          <div
            className="modal modal--library"
            role="dialog"
            aria-modal="true"
            aria-labelledby="library-modal-title"
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => {
              if (event.key === "Escape") setIsLibraryOpen(false);
            }}
          >
            <div className="modal__header">
              <h2 id="library-modal-title">Saved patterns</h2>
              <button
                type="button"
                className="button"
                onClick={() => setIsLibraryOpen(false)}
              >
                Close
              </button>
            </div>
            <label className="library-browser__search">
              <span className="library-browser__search-label">
                Search patterns
              </span>
              <input
                type="text"
                value={libraryQuery}
                onChange={(event) => setLibraryQuery(event.target.value)}
                placeholder="Search by name"
                autoFocus
              />
            </label>
            <p className="library-browser__count">
              {libraryPatterns.length}{" "}
              {libraryPatterns.length === 1 ? "pattern" : "patterns"}
            </p>
            {libraryPatterns.length === 0 ? (
              <p className="collections-empty">No patterns match that search.</p>
            ) : (
              <div className="library-grid">
                {libraryPatterns.map((saved) => {
                  const colorCount = countDistinctBeadColors(
                    saved.pattern,
                    defaultColor
                  );
                  const selected =
                    String(saved.id) === String(effectiveSelectedSavedId);
                  return (
                    <button
                      key={saved.id}
                      type="button"
                      data-pattern-id={saved.id}
                      className={`library-card${
                        selected ? " is-selected" : ""
                      }`}
                      aria-label={`${saved.name}, ${saved.cols} around, ${colorCount} ${
                        colorCount === 1 ? "color" : "colors"
                      }`}
                      aria-pressed={selected}
                      onClick={() => {
                        setSelectedPatternId(String(saved.id));
                        handleLoadSaved(saved.id);
                        setIsLibraryOpen(false);
                      }}
                    >
                      <PatternThumb
                        pattern={saved.pattern}
                        cols={saved.cols}
                        rows={saved.rows}
                        defaultColor={defaultColor}
                      />
                      <span className="library-card__name">{saved.name}</span>
                      <span className="library-card__meta">
                        {saved.cols} around · {colorCount}{" "}
                        {colorCount === 1 ? "color" : "colors"}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
      {isDissolveModalOpen && (
        <div
          className="modal-overlay"
          onClick={() => setIsDissolveModalOpen(false)}
        >
          <div
            className="modal modal--dissolve"
            role="dialog"
            aria-modal="true"
            aria-labelledby="dissolve-modal-title"
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            <div className="modal__header">
              <h2 id="dissolve-modal-title">Dissolve</h2>
              <button
                type="button"
                className="button"
                onClick={() => setIsDissolveModalOpen(false)}
              >
                Close
              </button>
            </div>
            <DissolvePanel
              pattern={pattern}
              cols={cols}
              rows={rows}
              defaultColor={defaultColor}
              keptRows={dissolveKeptRows}
              onDissolve={handleDissolve}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
